import type { ApolloCache } from '@apollo/client';
import { isApolloError } from '@apollo/client';
import type { CacheFieldName } from '@risksmart-app/web-graphql-client/generated/graphql';
import { isTRPCClientError } from '@trpc/client';
import type { ZodType } from 'zod';

export type { CacheFieldName };

export const evictField = <T>(
  cache: ApolloCache<T>,
  fieldName: CacheFieldName
) => {
  cache.evict({
    id: 'ROOT_QUERY',
    fieldName: fieldName,
  });
  cache.gc();
};

export const isPermissionError = (e: unknown) => {
  return (
    e instanceof Error &&
    isApolloError(e) &&
    e.graphQLErrors.length > 0 &&
    e.graphQLErrors[0].extensions?.code === 'permission-error'
  );
};

export enum HasuraErrorCodes {
  ConstraintError = 'constraint-violation',
  PermissionError = 'permission-error',
  UnexpectedError = 'unexpected',
  ValidationFailed = 'validation-failed',
}

/**
 * Error accessors for the GraphQL→tRPC bridge (COI-577). Each takes `unknown`
 * and returns a plain value, never the Apollo error object, so call-sites stay
 * off Apollo-shaped access.
 *
 * There is deliberately no raw `cause` / `graphQLErrors` accessor:
 * `src/utils/formError.ts` owns the structured-cause contract and must not gain
 * a bypass route.
 *
 * The getters return `undefined` for a plain `Error` and the predicates return
 * `false`; a call-site wanting a fallback narrows it itself.
 *
 * Most are leg-agnostic — they answer for an Apollo error or a tRPC one — but
 * a few are Apollo-only, and say so. Reach for an Apollo-only one when a wrong
 * answer on the tRPC leg would be a behaviour change rather than a harmless
 * one, and for `isGraphQLError`, which exists precisely to tell the legs apart.
 */
const asApolloError = (e: unknown) =>
  e instanceof Error && isApolloError(e) ? e : undefined;

/**
 * True when the error came from the Apollo (GraphQL) leg.
 *
 * The leg discriminator, not an accessor: it exists for a call-site that must
 * branch on which leg failed — `formError.ts` maps an Apollo failure and a tRPC
 * one to different shapes, so the Apollo branch must not swallow a tRPC error
 * the branch below it handles differently.
 *
 * Prefer a leg-agnostic accessor wherever the answer is the same either way.
 */
export const isGraphQLError = (e: unknown): boolean =>
  asApolloError(e) !== undefined;

/** Every GraphQL error message joined, or the tRPC message. */
export const getAllErrorMessages = (e: unknown): string | undefined => {
  const apollo = asApolloError(e);
  if (apollo) {
    return apollo.graphQLErrors.map((error) => error.message).join(' ');
  }
  if (isTRPCClientError(e)) {
    return e.message;
  }

  return undefined;
};

/** The first GraphQL error message, or the tRPC message. */
export const getFirstErrorMessage = (e: unknown): string | undefined => {
  const apollo = asApolloError(e);
  if (apollo) {
    return apollo.graphQLErrors[0]?.message;
  }
  if (isTRPCClientError(e)) {
    return e.message;
  }

  return undefined;
};

/**
 * The first GraphQL error's `extensions.code`, or the tRPC error's data code.
 * A non-string Apollo code returns `undefined` rather than being cast.
 */
export const getErrorCode = (e: unknown): string | undefined => {
  const apollo = asApolloError(e);
  if (apollo) {
    const code = apollo.graphQLErrors[0]?.extensions?.code;

    return typeof code === 'string' ? code : undefined;
  }
  if (isTRPCClientError(e)) {
    return e.data?.code;
  }

  return undefined;
};

/**
 * True when ANY GraphQL error carries this exact message, or when a tRPC
 * error's own message matches.
 *
 * Scans every error rather than reading `graphQLErrors[0]` like the accessors
 * above: Hasura returns a signal such as the optimistic-lock conflict alongside
 * unrelated errors, so a first-error read silently misses it.
 */
export const hasErrorMessage = (e: unknown, message: string): boolean => {
  const apollo = asApolloError(e);
  if (apollo) {
    return apollo.graphQLErrors.some((error) => error.message === message);
  }
  if (isTRPCClientError(e)) {
    return e.message === message;
  }

  return false;
};

/**
 * True when ANY GraphQL error message CONTAINS this substring.
 *
 * Apollo only, unlike `hasErrorMessage` above — the tRPC leg reports the same
 * conditions through typed causes that `formError.ts` reads, so answering for a
 * tRPC error here would let a message-shaped match pre-empt the typed handling
 * and change behaviour. Substring rather than equality because the server
 * appends context to some of these sentences.
 */
export const someGraphQLErrorIncludes = (
  e: unknown,
  substring: string
): boolean =>
  asApolloError(e)?.graphQLErrors.some((error) =>
    error.message.includes(substring)
  ) ?? false;

/**
 * True when ANY GraphQL error carries this `extensions.code`, or when a tRPC
 * error's data code matches. Same all-errors scan as `hasErrorMessage`.
 */
export const hasErrorCode = (e: unknown, code: string): boolean => {
  const apollo = asApolloError(e);
  if (apollo) {
    return apollo.graphQLErrors.some(
      (error) => error.extensions?.code === code
    );
  }
  if (isTRPCClientError(e)) {
    return e.data?.code === code;
  }

  return false;
};

/**
 * The first GraphQL error's `extensions`, validated against a caller-supplied
 * schema so the call-site declares its expected shape without an `as` cast.
 *
 * Apollo only — the tRPC leg surfaces this as a structured cause read through
 * `formError.ts`.
 */
export const getErrorExtensions = <T>(
  e: unknown,
  schema: ZodType<T>
): T | undefined => {
  const apollo = asApolloError(e);
  if (!apollo) {
    return undefined;
  }
  const parsed = schema.safeParse(apollo.graphQLErrors[0]?.extensions);

  return parsed.success ? parsed.data : undefined;
};
